import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, qs } from "../api/client";
import { PageHeader } from "../components/Layout";
import { useToast } from "../components/Toast";
import {
  Avatar,
  Badge,
  ConfirmModal,
  EmptyState,
  Field,
  IconAlert,
  IconEdit,
  IconSearch,
  IconStar,
  Modal,
  Spinner,
} from "../components/ui";

export default function Spotlight() {
  const toast = useToast();

  const [members, setMembers] = useState([]);
  const [incompleteCount, setIncompleteCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);

  const [selected, setSelected] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/spotlight${qs({ search })}`);
      setMembers(res.members);
      setIncompleteCount(res.incompleteCount);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast identity is stable
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = onlyIncomplete
    ? members.filter((m) => !m.featured_bio || !m.featured_photo_url)
    : members;

  function toggle(id) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function removeSelected() {
    setBusy(true);
    try {
      const { updated } = await api.post("/spotlight/bulk-remove", { ids: [...selected] });
      toast.success(`Removed ${updated} from the Spotlight.`);
      setSelected(new Set());
      setConfirming(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeOne(member) {
    try {
      await api.post(`/spotlight/${member.id}/remove`);
      toast.success(`${member.name} removed from the Spotlight.`);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  return (
    <>
      <PageHeader
        title="Leader Spotlight"
        subtitle={`${members.length} featured on the public site`}
      >
        <Link className="btn btn-sm btn-primary" to="/members?featured=false">
          <IconStar />
          Feature someone
        </Link>
      </PageHeader>

      <div className="content">
        {incompleteCount > 0 && (
          <div
            className="panel"
            style={{
              padding: "13px 18px",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 11,
              borderColor: "rgba(180,83,9,0.3)",
              background: "var(--amber-soft)",
            }}
          >
            <IconAlert style={{ width: 17, height: 17, color: "var(--amber)", flex: "0 0 auto" }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--amber)" }}>
              {incompleteCount} featured {incompleteCount === 1 ? "leader is" : "leaders are"} missing a
              bio or photo — they show as a plain initial on the public wall.
            </span>
            <span style={{ marginLeft: "auto" }}>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setOnlyIncomplete((v) => !v)}
              >
                {onlyIncomplete ? "Show all" : "Show only these"}
              </button>
            </span>
          </div>
        )}

        <div className="toolbar" style={{ marginBottom: 16 }}>
          <span className="search-wrap">
            <IconSearch />
            <input
              className="input"
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search featured leaders…"
            />
          </span>
          {selected.size > 0 && (
            <>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{selected.size} selected</span>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => setConfirming(true)}
              >
                Remove from Spotlight
              </button>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </>
          )}
        </div>

        {loading ? (
          <div className="spot-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="spot-card" key={i}>
                <div className="spot-card-top">
                  <div className="skeleton" style={{ width: 32, height: 32, borderRadius: "50%" }} />
                  <div style={{ flex: 1 }}>
                    <div className="skeleton" style={{ width: "55%", marginBottom: 6 }} />
                    <div className="skeleton" style={{ width: "40%", height: 10 }} />
                  </div>
                </div>
                <div className="skeleton" style={{ height: 40 }} />
              </div>
            ))}
          </div>
        ) : !visible.length ? (
          <div className="panel">
            <EmptyState
              icon={IconStar}
              title={search || onlyIncomplete ? "Nothing matches" : "No one is featured yet"}
              message={
                search || onlyIncomplete
                  ? "Try a different search, or clear the filter."
                  : "Pick members from the Members page to feature them on the public Leader Spotlight."
              }
              action={
                <Link className="btn btn-primary btn-sm" to="/members">
                  Go to Members
                </Link>
              }
            />
          </div>
        ) : (
          <div className="spot-grid">
            {visible.map((m) => {
              const incomplete = !m.featured_bio || !m.featured_photo_url;
              return (
                <article
                  key={m.id}
                  className={`spot-card ${selected.has(m.id) ? "is-selected" : ""} ${
                    incomplete ? "is-incomplete" : ""
                  }`}
                >
                  <div className="spot-card-top">
                    <Avatar name={m.name} photo={m.featured_photo_url} id={m.id} />
                    <div style={{ minWidth: 0 }}>
                      <h4>{m.name}</h4>
                      {m.designation && <div className="role">{m.designation}</div>}
                      <div className="company">{m.company}</div>
                    </div>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selected.has(m.id)}
                      onChange={() => toggle(m.id)}
                      aria-label={`Select ${m.name}`}
                    />
                  </div>

                  <div className="spot-preview-label">Public bio</div>
                  {m.featured_bio ? (
                    <p style={{ fontSize: 13, color: "var(--text-muted)" }}>{m.featured_bio}</p>
                  ) : (
                    <Badge tone="amber">No bio written</Badge>
                  )}

                  {!m.featured_photo_url && (
                    <div style={{ marginTop: 8 }}>
                      <Badge tone="amber">No photo</Badge>
                    </div>
                  )}

                  <div className="spot-card-actions">
                    <button type="button" className="btn btn-sm" onClick={() => setEditing(m)}>
                      <IconEdit />
                      Edit
                    </button>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => removeOne(m)}>
                      Remove
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <SpotlightModal
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            setMembers((list) => list.map((m) => (m.id === saved.id ? saved : m)));
            setIncompleteCount(
              (c) => c - (editing.featured_bio && editing.featured_photo_url ? 0 : 1) +
                (saved.featured_bio && saved.featured_photo_url ? 0 : 1)
            );
            toast.success(`${saved.name}'s spotlight updated.`);
          }}
        />
      )}

      {confirming && (
        <ConfirmModal
          title={`Remove ${selected.size} from the Spotlight?`}
          message="They stay in the members list — they just stop showing on the public Leader Spotlight wall."
          confirmLabel="Remove"
          onConfirm={removeSelected}
          onClose={() => setConfirming(null)}
          busy={busy}
        />
      )}
    </>
  );
}

const BIO_LIMIT = 600;

function SpotlightModal({ member, onClose, onSaved }) {
  const [bio, setBio] = useState(member.featured_bio || "");
  const [photo, setPhoto] = useState(member.featured_photo_url || "");
  const [fields, setFields] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setFields({});

    try {
      const { member: saved } = await api.patch(`/spotlight/${member.id}`, {
        featured_bio: bio,
        featured_photo_url: photo,
      });
      onSaved(saved);
    } catch (err) {
      setError(err.message);
      setFields(err.fields || {});
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Spotlight — ${member.name}`}
      subtitle="This is exactly what the public site shows."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="spotlight-form" className="btn btn-primary" disabled={busy}>
            {busy && <Spinner />}
            Save
          </button>
        </>
      }
    >
      <form id="spotlight-form" onSubmit={submit} noValidate>
        {error && (
          <div className="login-alert" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        <Field
          label="Photo URL"
          error={fields.featured_photo_url}
          hint="Leave blank to show their initial in a coloured circle instead."
        >
          <input
            className={`input ${fields.featured_photo_url ? "has-error" : ""}`}
            type="url"
            value={photo}
            onChange={(e) => setPhoto(e.target.value)}
            placeholder="https://…"
          />
        </Field>

        <Field
          label="Short bio"
          error={fields.featured_bio}
          hint={`${bio.length}/${BIO_LIMIT} characters`}
        >
          <textarea
            className={`textarea ${fields.featured_bio ? "has-error" : ""}`}
            value={bio}
            maxLength={BIO_LIMIT}
            onChange={(e) => setBio(e.target.value)}
            rows={4}
            placeholder="One or two sentences on what makes their work worth following."
          />
        </Field>

        <div className="spot-preview-label">Preview on the public wall</div>
        <div className="spot-preview">
          <Avatar name={member.name} photo={photo} id={member.id} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{member.name}</div>
            {member.designation && (
              <div style={{ fontSize: 11.5, color: "var(--green)", fontWeight: 600 }}>
                {member.designation}
              </div>
            )}
            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{member.company}</div>
          </div>
        </div>
      </form>
    </Modal>
  );
}
